import moment from "moment";
import nzhcn from "nzh/cn";

const FORCE_FORGE_RSA_ENCRYPTION = false;
const RSA_CRYPTO_UNAVAILABLE_MESSAGE = "当前浏览器加密能力不可用,请升级或者改用新版chrome浏览器";


export const isNumeric = str => {
  if (typeof str === "number") {
    return true;
  } else if (typeof str === "string") {
    if (str.indexOf(",") >= 0) {
      //包含千分号
      const thousandSeperatorPreg = /^[-+]?[\d]{0,3}(?=(,[\d]{3})*(\.[\d]{0,2})?$)/;
      return thousandSeperatorPreg.test("1,234.00");
    } else {
      //没有千分号
      return /^[-+]?[\d]+(\.[\d]{0,2})?$/.test(str);
    }
  }
};

export const parseNumber = str => {
  if (typeof str === "number") {
    return str;
  }
  if (isNumeric(str)) {
    return parseFloat(str.replace(/,/g, ""));
  } else {
    return str;
  }
};

export function round(num, point = 2) {
  if (parseFloat(num) == num) {
    num = parseFloat(num)
    return parseFloat(num.toFixed(point));
  } else {
    return 0
  }

}

export function toFixed(num, point = 2) {
  return Number.parseFloat(num).toFixed(point)
}

export function percentage(num, point = 2) {
  return (Math.round(num * 100 * 10000) / 10000).toFixed(point) + "%";
}

export function dateRange(fromDate, toDate) {
  return fromDate == toDate ? fromDate : `${fromDate} - ${toDate}`;
}

/**
 * 格式化文本
 * @param text
 * @returns {*}
 */
export function formatText(text) {
  if (!text) {
    return "";
  }
  text = text.replace(/[\r]?([^\n]+)/g, '<div>$1</div>')
  return text
}

/**
 * 把阿拉伯数字转成中文
 * @param number
 * @returns {*}
 */
export function chineseNumber(number) {
  return nzhcn.encodeS(number);
}

/**
 * 把数字不足2位的左补0
 */
export function padNumber(number, length = 2) {
  return number == null ? '' : number.toString().padStart(length, '0')
}

function hasWebCryptoRsaSupport() {
  return !FORCE_FORGE_RSA_ENCRYPTION
    && typeof window !== 'undefined'
    && window.crypto
    && window.crypto.subtle
    && window.crypto.subtle.importKey
    && window.crypto.subtle.encrypt;
}

async function encryptPasswordWithWebCrypto(b64PublicKey, data) {
  const binaryDer = Uint8Array.from(atob(b64PublicKey), (c) => c.charCodeAt(0));
  const cryptoKey = await window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  );
  const enc = new TextEncoder().encode(data);
  const buf = await window.crypto.subtle.encrypt({ name: "RSA-OAEP" }, cryptoKey, enc);
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]);
  }
  return btoa(bin);
}

async function encryptPasswordWithForge(b64PublicKey, data) {
  const forgeModule = await import('node-forge');
  const forge = forgeModule.default || forgeModule;
  const pemPublicKey = [
    "-----BEGIN PUBLIC KEY-----",
    b64PublicKey.match(/.{1,64}/g).join("\n"),
    "-----END PUBLIC KEY-----"
  ].join("\n");
  const publicKey = forge.pki.publicKeyFromPem(pemPublicKey);
  const encrypted = publicKey.encrypt(forge.util.encodeUtf8(data), 'RSA-OAEP', {
    md: forge.md.sha256.create(),
    mgf1: {
      md: forge.md.sha256.create()
    }
  });
  return forge.util.encode64(encrypted);
}

/**
 * 使用 RSA-OAEP + SHA-256 加密（与后端 Java RSA/ECB/OAEPWithSHA-256AndMGF1Padding 一致）
 * 使用 Web Crypto API，在 localhost/127.0.0.1(HTTP) 及所有 HTTPS 环境下均可用。
 * @param {string} publicKey PEM 带头尾 或 纯 Base64 的 X.509(SPKI) 公钥
 * @param {string} data 明文密码
 * @returns {Promise<string|false>}
 */
export async function encryptedPassword(publicKey, data) {
  // 统一去掉 PEM 头尾和所有空白，得到纯 Base64
  const b64 = publicKey
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s/g, "");

  try {
    if (hasWebCryptoRsaSupport()) {
      return await encryptPasswordWithWebCrypto(b64, data);
    }
    console.warn("crypto.subtle 不可用，使用 node-forge 降级方案");
    return await encryptPasswordWithForge(b64, data);
  } catch (e) {
    console.error("RSA-OAEP 加密失败", e);
    return false;
  }
}

export { RSA_CRYPTO_UNAVAILABLE_MESSAGE };
